import Engine from './core/engine.js';
import CatalogPanel from './ui/catalog-panel.js';
import Toolbar from './ui/toolbar.js';
import ProjectManager from './managers/project-manager.js';
import Tutorial from './ui/tutorial.js';
import FirstPersonController from './ui/first-person.js';

class App {
  constructor() {
    this.engine = null;
    this.catalogPanel = null;
    this.toolbar = null;
    this.projectManager = null;
    this.buttons = {};
    this.isLocked = false;
    this.tutorial = null;
    this.firstPersonController = null;
  }

  async init() {
    const container = document.getElementById('canvas-container');
    this.engine = new Engine(container);
    this.engine.init();
    this.engine.start();
    
    // Inizializza project manager
    this.projectManager = new ProjectManager(this.engine);

    // Inizializza controller prima persona
    this.firstPersonController = new FirstPersonController(this.engine);

    await this.setupUI();
    this.setupKeyboardShortcuts();

    // Rende App accessibile globalmente
    window.app = this;

    if (Tutorial.shouldAutoStart()) {
      setTimeout(() => this.startTutorial(), 800);
    }
    
    console.log('✓ Applicazione pronta');
  }

  async setupUI() {
    this.createInfoPanel();
    
    // Inizializza catalogo mobili
    const furnitureController = this.engine.getFurnitureController();
    this.catalogPanel = new CatalogPanel(furnitureController);
    await this.catalogPanel.init();
    
    // Passa l'inputController al catalogPanel per disabilitare comandi tastiera durante ricerca
    const inputController = this.engine.getInputController();
    this.catalogPanel.setInputController(inputController);

    // Passa il dragDropManager al catalogPanel per il touch drag
    const dragDropManager = this.engine.dragDropManager;
    if (dragDropManager) this.catalogPanel.setDragDropManager(dragDropManager);

    // Inizializza toolbar
    this.toolbar = new Toolbar(this);
    this.toolbar.init();

    // Aggancia il callback di selezione per aggiornare i bottoni del pannello info
    const selectionController = this.engine.getSelectionController();
    if (selectionController) {
      selectionController.onSelectionChange = () => this.updateInfoPanelButtons();
    }

    // Aggancia i callback di porte e finestre per il bottone "Apri/Chiudi tutto"
    const doorController   = this.engine.getDoorController();
    const windowController = this.engine.getWindowController();
    if (doorController)   doorController.onOpeningsChange   = () => this.updateInfoPanelButtons();
    if (windowController) windowController.onOpeningsChange = () => this.updateInfoPanelButtons();
  }

  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (event) => {
      // Ignora se l'utente sta scrivendo in un input
      const target = event.target;
      if (target.tagName === 'INPUT' || 
          target.tagName === 'TEXTAREA' || 
          target.tagName === 'SELECT' ||
          target.isContentEditable) {
        return;
      }

      // S - Salva progetto
      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        this.toolbar.showSaveDialog();
      }

      // A - Aggiorna progetto
      if (event.key === 'a' || event.key === 'A') {
        event.preventDefault();
        this.updateProject();
      }

      // C - Carica progetto
      if (event.key === 'c' || event.key === 'C') {
        event.preventDefault();
        this.toolbar.showLoadDialog();
      }

      // L - Toggle lucchetto
      if (event.key === 'l' || event.key === 'L') {
        this.toggleLock();
        event.preventDefault();
      }

      // T - Avvia tutorial
      if (event.key === 't' || event.key === 'T') {
        event.preventDefault();
        this.startTutorial();
      }

      // P - Prima persona
      if (event.key === 'p' || event.key === 'P') {
        event.preventDefault();
        this.toggleFirstPerson();
      }
    });
  }

  // ========== GESTIONE LUCCHETTO ==========

  setLocked(locked) {
    this.isLocked = locked;

    const selectionController = this.engine.getSelectionController();
    if (selectionController) selectionController.setLocked(locked);

    const dragDropManager = this.engine.dragDropManager;
    if (dragDropManager) dragDropManager.isLocked = locked;

    // Aggiorna toolbar
    if (this.toolbar) this.toolbar.updateLockButton();

    console.log(`Progetto ${locked ? 'bloccato 🔒' : 'sbloccato 🔓'}`);
  }

  toggleLock() {
    this.setLocked(!this.isLocked);
  }

  createInfoPanel() {
    const infoPanel = document.getElementById('info-panel');
    if (!infoPanel) return;

    infoPanel.innerHTML = `
      <div class="info-buttons-bar">
        <button id="btn-wall" class="toolbar-button" title="Disegna Parete (W)">
          <span class="toolbar-icon">🧱</span>
        </button>
        <button id="btn-rotate" class="toolbar-button" title="Ruota (R)" disabled>
          <span class="toolbar-icon">🔃</span>
        </button>
        <button id="btn-duplicate" class="toolbar-button" title="Duplica (D)" disabled>
          <span class="toolbar-icon">📋</span>
        </button>
        <button id="btn-delete" class="toolbar-button" title="Elimina (Canc)" disabled>
          <span class="toolbar-icon">❌</span>
        </button>
        <button id="btn-toggle-all" class="toolbar-button" title="Apri/Chiudi tutto (O)" disabled>
          <span class="toolbar-icon">▶️</span>
        </button>
        <button id="btn-first-person" class="toolbar-button" title="Vista in prima persona (P)">
          <span class="toolbar-icon">🧍</span>
        </button>
        <button id="btn-tutorial" class="toolbar-button" title="Tutorial (T)">
          <span class="toolbar-icon">🎓</span>
        </button>
      </div>      
    `;

    // Salva riferimenti ai bottoni
    this.buttons.wall = document.getElementById('btn-wall');
    this.buttons.rotate = document.getElementById('btn-rotate');
    this.buttons.delete = document.getElementById('btn-delete');
    this.buttons.duplicate = document.getElementById('btn-duplicate');
    this.buttons.toggleAll = document.getElementById('btn-toggle-all');
    this.buttons.firstPerson = document.getElementById('btn-first-person');
    this.buttons.tutorial = document.getElementById('btn-tutorial');

    // Event listeners
    this.buttons.wall.addEventListener('click', () => this.toggleWallMode());
    this.buttons.rotate.addEventListener('click', () => this.rotateSelected());
    this.buttons.delete.addEventListener('click', () => this.deleteSelected());
    this.buttons.duplicate.addEventListener('click', () => this.duplicateSelected());
    this.buttons.toggleAll.addEventListener('click', () => this.toggleAllOpenings());
    this.buttons.firstPerson.addEventListener('click', () => this.toggleFirstPerson());
    this.buttons.tutorial.addEventListener('click', () => this.startTutorial());

    // Stato iniziale
    this.updateAllButtons();
  }

  // ========== GESTIONE PROGETTI ==========
  newProject() {
    this.projectManager.newProject();
    this.updateAllButtons();
  }

  async saveProject(projectName) {
    try {
      await this.projectManager.saveProject(projectName);
      this.toolbar.updateButtons();
      alert(`Progetto "${projectName}" salvato con successo!`);
    } catch (error) {
      console.error('Errore salvataggio:', error);
      throw error;
    }
  }

  async updateProject() {
    if (!this.projectManager.hasCurrentProject()) {
      alert('Carica prima un progetto per aggiornarlo');
      return;
    }

    try {
      await this.projectManager.updateProject();
      this.toolbar.updateButtons();
      alert('Progetto aggiornato con successo!');
    } catch (error) {
      alert('Errore durante l\'aggiornamento: ' + error.message);
      console.error(error);
    }
  }

  async loadProject(projectId) {
    try {
      await this.projectManager.loadProject(projectId);
      this.toolbar.updateButtons();
    } catch (error) {
      console.error('Errore caricamento:', error);
      throw error;
    }
  }

  async getAllProjects() {
    return await this.projectManager.getAllProjects();
  }

  async deleteProject(projectId) {
    await this.projectManager.deleteProject(projectId);
    this.toolbar.updateButtons();
  }

  // ========== OPERAZIONI PARETI ==========

  toggleWallMode() {
    const wallController = this.engine.getWallController();
    wallController.toggleDrawMode();
    this.updateAllButtons();
  }
  
  // ========== OPERAZIONI MOBILI ==========

  rotateSelected() {
    const selectionController = this.engine.getSelectionController();
    selectionController.rotateSelected(45);
    this.toolbar.updateButtons();
  }
  
  deleteSelected() {
    const selectionController = this.engine.getSelectionController();
    selectionController.deleteSelected();
    this.toolbar.updateButtons();
  }
  
  duplicateSelected() {
    const selectionController = this.engine.getSelectionController();
    selectionController.duplicateSelected();
    this.toolbar.updateButtons();
  }
  
  toggleAllOpenings() {
    const doorController = this.engine.getDoorController();
    const windowController = this.engine.getWindowController();
    
    const allOpen = doorController.getAllDoors().every(d => d.isOpen) &&
                    windowController.getAllWindows().every(w => w.isOpen);
    
    if (allOpen) {
      // Chiude tutto
      doorController.getAllDoors().forEach(d => d.close());
      windowController.getAllWindows().forEach(w => w.close());
      console.log('Chiuse tutte le porte e finestre');
    } else {
      // Apre tutto
      doorController.getAllDoors().forEach(d => d.open());
      windowController.getAllWindows().forEach(w => w.open());
      console.log('Aperte tutte le porte e finestre');
    }
  }

  updateAllButtons() {
    const wallController = this.engine.getWallController();

    // Aggiorna bottone pareti (solo classe active, l'icona 🧱 rimane fissa)
    if (wallController.drawingMode) {
      this.buttons.wall?.classList.add('active');
      if (this.buttons.wall) this.buttons.wall.title = 'Stop Disegno (W)';
    } else {
      this.buttons.wall?.classList.remove('active');
      if (this.buttons.wall) this.buttons.wall.title = 'Disegna Parete (W)';
    }

    // Aggiorna bottone prima persona
    if (this.firstPersonController?.isActive) {
      this.buttons.firstPerson?.classList.add('active');
    } else {
      this.buttons.firstPerson?.classList.remove('active');
    }

    // Aggiorna stato enabled/disabled dei bottoni di selezione
    this.updateInfoPanelButtons();

    // Aggiorna toolbar
    if (this.toolbar) {
      this.toolbar.updateButtons();
    }
  }

  updateInfoPanelButtons() {
    const selectionController = this.engine?.getSelectionController();
    if (!selectionController) return;

    const hasSelection =
      selectionController.selectedFurniture.length > 0 ||
      selectionController.selectedDoors.length     > 0 ||
      selectionController.selectedWindows.length   > 0 ||
      selectionController.selectedWalls.length     > 0;

    if (this.buttons.rotate)    this.buttons.rotate.disabled    = !hasSelection;
    if (this.buttons.duplicate) this.buttons.duplicate.disabled = !hasSelection;
    if (this.buttons.delete)    this.buttons.delete.disabled    = !hasSelection;

    // Abilita "Apri/Chiudi tutto" solo se ci sono porte o finestre nella scena
    const doorController   = this.engine?.getDoorController();
    const windowController = this.engine?.getWindowController();
    const hasOpenings = (doorController?.getAllDoors().length   > 0) ||
                        (windowController?.getAllWindows().length > 0);
    if (this.buttons.toggleAll) this.buttons.toggleAll.disabled = !hasOpenings;
  }

  toggleFirstPerson() {
    const fp = this.firstPersonController;
    if (!fp) return;

    if (fp.isActive) {
      fp.exit();
      this.buttons.firstPerson?.classList.remove('active');
    } else {
      // Disattiva modalità incompatibili prima di entrare
      this.engine.getWallController().cancelDrawing?.();
      this.engine.getDoorController().disablePlacementMode?.();
      this.engine.getWindowController().disablePlacementMode?.();
      fp.enter();
      this.buttons.firstPerson?.classList.add('active');
    }
  }

  startTutorial() {
    if (this.tutorial?.isActive) return;
    this.tutorial = new Tutorial();
    this.tutorial.start();
  }
}

// Avvio applicazione
const app = new App();
app.init();